import { Box, Container, Group, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { IconBolt, IconFileImport, IconWand } from '@tabler/icons-react';

import { Reveal } from '@/components/motion/Reveal';
import classes from './HowItWorks.module.css';

const STEPS = [
  {
    n: '01',
    icon: IconFileImport,
    title: 'Import your map',
    body:
      'Drag in your stock or aftermarket map file. Clancy Tuning auto-detects the ECU platform and surfaces every table — fuel, spark, knock, transmission, the works.',
  },
  {
    n: '02',
    icon: IconWand,
    title: 'Edit with AI',
    body:
      'Work in 2D heatmaps or 3D surfaces. Ask "Why is this cell flagged?" and get a plain-English answer — pinned to the deterministic verdict, not invented by the LLM.',
  },
  {
    n: '03',
    icon: IconBolt,
    title: 'Flash safely',
    body:
      'Every change gets a verdict. Danger blocks. Warning needs your ack. The override log captures who, what, and why — so you always have a paper trail.',
  },
] as const;

export function HowItWorks() {
  return (
    <Box component="section" id="how-it-works" py={{ base: 64, md: 96 }} className={classes.wrap} aria-labelledby="how-title">
      <Container size="xl">
        <Stack align="center" ta="center" gap="md" mb={56}>
          <Reveal>
            <Text size="sm" tt="uppercase" lts="0.12em" c="var(--tf-forge-start)" fw={700}>
              How it works
            </Text>
          </Reveal>
          <Reveal delay={0.05}>
            <Title id="how-title" order={2} className={classes.heading}>
              From base map to safe flash — in three steps.
            </Title>
          </Reveal>
        </Stack>

        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xl">
          {STEPS.map((step, idx) => {
            const Icon = step.icon;
            return (
              <Reveal key={step.n} delay={idx * 0.08}>
                <Box className={classes.step}>
                  <Group gap="md" mb="md" align="center">
                    <Text className={classes.stepNumber}>{step.n}</Text>
                    <Box className={classes.stepIcon} aria-hidden="true">
                      <Icon size={22} stroke={1.8} />
                    </Box>
                  </Group>
                  <Title order={3} fz="h4" mb="xs" c="var(--tf-text-primary)">
                    {step.title}
                  </Title>
                  <Text c="var(--tf-text-secondary)" size="sm" lh={1.6}>
                    {step.body}
                  </Text>
                  {idx < STEPS.length - 1 && (
                    <div className={classes.connector} aria-hidden="true" />
                  )}
                </Box>
              </Reveal>
            );
          })}
        </SimpleGrid>
      </Container>
    </Box>
  );
}
